// Path: C:\gestion-copro\src\services\crm.service.ts
// Session 187 : Module CRM — Tunnel de vente, prospects, commissions
// Session 189 : Enrichissement prospect pour SENTINELLE moteur de prospection
// Session 189 : Responsables dynamiques — chargerResponsables() depuis Firestore
// Session 194 : ajouterHistoriqueMail persiste contenuHtml — brouillon ouvrable depuis Messagerie
// Session 199 : Documents prospect (Storage + sous-collection) + conversation SENTINELLE persistée
// Session 206 : importerReponseProspect + enregistrerAnalyseReponse — réponses entrantes PDF
// Session 207 : lierProspectUserManuellement — liaison manuelle prospect ↔ user (commissions branchées)

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  setDoc,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from './firebase';
import {
  Prospect,
  NoteSuivi,
  StatutProspect,
  ResponsableProspect,
  TypeCibleProspect,
  TypeProspect,
  SegmentProspect,
  TypeTemplateMail,
  HistoriqueMailProspect,
  InfoResponsable,
  Commission,
  DetailCommissionClient,
  DocumentProspect,
  TypeDocumentProspect,
  MessageConversationProspect,
  AnalyseReponseProspect,
  TAUX_COMMISSION,
  TAUX_TVA,
  PRIX_TTC_MENSUEL,
  calculerCAHTMensuel,
  calculerCommissionMensuelle,
} from '../types/crm.types';

const COLLECTION_PROSPECTS = 'crm_prospects';
const COLLECTION_COMMISSIONS = 'crm_commissions';

// ============================================
// COULEURS CYCLIQUES POUR RESPONSABLES
// ============================================

const COULEURS_RESPONSABLES = [
  { bg: 'bg-emerald-100', text: 'text-emerald-700', hover: 'hover:bg-emerald-200', border: 'border-emerald-400' },
  { bg: 'bg-blue-100', text: 'text-blue-700', hover: 'hover:bg-blue-200', border: 'border-blue-400' },
  { bg: 'bg-purple-100', text: 'text-purple-700', hover: 'hover:bg-purple-200', border: 'border-purple-400' },
  { bg: 'bg-amber-100', text: 'text-amber-700', hover: 'hover:bg-amber-200', border: 'border-amber-400' },
  { bg: 'bg-rose-100', text: 'text-rose-700', hover: 'hover:bg-rose-200', border: 'border-rose-400' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700', hover: 'hover:bg-cyan-200', border: 'border-cyan-400' },
];

// ============================================
// RESPONSABLES — CHARGEMENT DYNAMIQUE
// ============================================

/**
 * Charge la liste des responsables disponibles depuis Firestore.
 * - L'admin (role === 'admin' ou 'admin_saas') est toujours inclus en premier.
 * - Les apporteurs (apporteurInfo.identifiant présent) sont ajoutés ensuite.
 * - Chaque responsable reçoit une couleur cyclique pour l'affichage.
 */
export const chargerResponsables = async (): Promise<InfoResponsable[]> => {
  const responsables: InfoResponsable[] = [];

  try {
    // 1. Charger les admins (role === 'admin' OU 'admin_saas')
    const rolesAdmin = ['admin', 'admin_saas'];
    for (const role of rolesAdmin) {
      const qAdmin = query(collection(db, 'users'), where('role', '==', role));
      const snapAdmin = await getDocs(qAdmin);
      for (const d of snapAdmin.docs) {
        const data = d.data();
        const identifiant = (data.prenom || data.displayName || data.email || 'admin')
          .split(' ')[0].toLowerCase();
        // Éviter les doublons (un user ne peut pas avoir deux rôles, mais sécurité)
        if (responsables.some((r) => r.identifiant === identifiant)) continue;
        const nomComplet = data.prenom && data.nom
          ? `${data.prenom} ${data.nom}`
          : (data.displayName || data.email || 'Admin');
        responsables.push({
          identifiant,
          nomComplet,
          couleurClasse: `${COULEURS_RESPONSABLES[0].bg} ${COULEURS_RESPONSABLES[0].text}`,
        });
      }
    }

    // 2. Charger tous les users et filtrer ceux qui ont apporteurInfo.identifiant
    const snapAll = await getDocs(collection(db, 'users'));
    for (const d of snapAll.docs) {
      const data = d.data();
      const apporteurId = data.apporteurInfo?.identifiant;
      if (apporteurId && !responsables.some((r) => r.identifiant === apporteurId)) {
        const colorIndex = responsables.length % COULEURS_RESPONSABLES.length;
        responsables.push({
          identifiant: apporteurId,
          nomComplet: data.apporteurInfo?.nomComplet || data.displayName || apporteurId,
          couleurClasse: `${COULEURS_RESPONSABLES[colorIndex].bg} ${COULEURS_RESPONSABLES[colorIndex].text}`,
        });
      }
    }
  } catch (error) {
    console.error('[CRM] Erreur chargement responsables:', error);
  }

  return responsables;
};

/**
 * Retourne le label d'un responsable depuis la liste chargée.
 * Fallback : retourne l'identifiant capitalisé si pas trouvé.
 */
export const getLabelResponsable = (
  identifiant: string,
  responsables: InfoResponsable[]
): string => {
  const found = responsables.find((r) => r.identifiant === identifiant);
  if (found) return found.nomComplet;
  return identifiant.charAt(0).toUpperCase() + identifiant.slice(1);
};

/**
 * Retourne la classe couleur d'un responsable.
 * Fallback : gris neutre.
 */
export const getCouleurResponsable = (
  identifiant: string,
  responsables: InfoResponsable[]
): string => {
  const found = responsables.find((r) => r.identifiant === identifiant);
  if (found) return found.couleurClasse;
  return 'bg-gray-100 text-gray-700';
};

// ============================================
// PROSPECTS — CRUD
// ============================================

/** Créer un nouveau prospect */
export const creerProspect = async (data: {
  nom: string;
  typeProspect: TypeProspect;
  typeCible: TypeCibleProspect;
  segment: SegmentProspect;
  responsable: ResponsableProspect;
  contactNom?: string;
  contactEmail?: string;
  contactTelephone?: string;
  adresse?: string;
  siteWeb?: string;
  source?: string;
  noteInitiale?: string;
  problematiques?: string[];
  objectifContact?: string;
  tailleStructure?: string;
  prochaineAction?: string;
  dateProchaineAction?: Timestamp;
}): Promise<string> => {
  const now = Timestamp.now();

  const notes: NoteSuivi[] = [];
  if (data.noteInitiale?.trim()) {
    notes.push({
      id: `note_${Date.now()}`,
      date: now,
      texte: data.noteInitiale.trim(),
      auteur: data.responsable,
    });
  }

  const prospect = {
    nom: data.nom.trim(),
    typeProspect: data.typeProspect,
    typeCible: data.typeCible,
    segment: data.segment,
    statut: 'identifie' as StatutProspect,
    responsable: data.responsable,
    contactNom: data.contactNom?.trim() || '',
    contactEmail: data.contactEmail?.trim() || '',
    contactTelephone: data.contactTelephone?.trim() || '',
    adresse: data.adresse?.trim() || '',
    siteWeb: data.siteWeb?.trim() || '',
    source: data.source?.trim() || '',
    notes,
    problematiques: data.problematiques || [],
    objectifContact: data.objectifContact?.trim() || '',
    tailleStructure: data.tailleStructure?.trim() || '',
    prochaineAction: data.prochaineAction?.trim() || '',
    dateProchaineAction: data.dateProchaineAction || null,
    historiqueMails: [],
    createdAt: now,
    updatedAt: now,
  };

  const ref = await addDoc(collection(db, COLLECTION_PROSPECTS), prospect);
  console.log(`[CRM] Prospect créé: ${ref.id} — ${data.nom}`);
  return ref.id;
};

/** Charger tous les prospects */
export const chargerProspects = async (): Promise<Prospect[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_PROSPECTS),
      orderBy('updatedAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as Prospect[];
  } catch (error) {
    console.error('[CRM] Erreur chargement prospects:', error);
    return [];
  }
};

/** Charger un prospect par son ID (lecture directe — compatible apporteur) */
export const chargerProspectParId = async (id: string): Promise<Prospect | null> => {
  try {
    const snap = await getDoc(doc(db, COLLECTION_PROSPECTS, id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as Prospect;
  } catch (error) {
    console.error('[CRM] Erreur chargement prospect par ID:', error);
    return null;
  }
};

/** Charger les prospects d'un responsable */
export const chargerProspectsParResponsable = async (
  responsable: ResponsableProspect
): Promise<Prospect[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_PROSPECTS),
      where('responsable', '==', responsable),
      orderBy('updatedAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as Prospect[];
  } catch (error) {
    console.warn('[CRM] Index manquant, fallback sans tri:', error);
    try {
      const q = query(
        collection(db, COLLECTION_PROSPECTS),
        where('responsable', '==', responsable)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Prospect[];
    } catch (fallbackError) {
      console.error('[CRM] Erreur chargement par responsable:', fallbackError);
      return [];
    }
  }
};

/** Mettre à jour un prospect */
export const mettreAJourProspect = async (
  prospectId: string,
  data: Partial<Omit<Prospect, 'id' | 'createdAt'>>
): Promise<void> => {
  // Filtrer les undefined — Firestore les rejette
  const dataFiltre = Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== undefined)
  );
  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    ...dataFiltre,
    updatedAt: Timestamp.now(),
  });
  console.log(`[CRM] Prospect mis à jour: ${prospectId}`);
};

/** Changer le statut d'un prospect (déplacement Kanban) */
export const changerStatutProspect = async (
  prospectId: string,
  nouveauStatut: StatutProspect
): Promise<void> => {
  const baseUpdate: { statut: StatutProspect; updatedAt: Timestamp } = {
    statut: nouveauStatut,
    updatedAt: Timestamp.now(),
  };

  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), baseUpdate);

  if (nouveauStatut === 'contacte') {
    const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
    if (prospectSnap.exists() && !prospectSnap.data().datePremierContact) {
      await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
        datePremierContact: Timestamp.now(),
      });
    }
  }

  if (nouveauStatut === 'converti') {
    await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
      dateConversion: Timestamp.now(),
    });
  }
  console.log(`[CRM] Prospect ${prospectId} → ${nouveauStatut}`);
};

/** Changer le responsable d'un prospect */
export const changerResponsableProspect = async (
  prospectId: string,
  responsable: ResponsableProspect
): Promise<void> => {
  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    responsable,
    updatedAt: Timestamp.now(),
  });
  console.log(`[CRM] Prospect ${prospectId} → responsable: ${responsable}`);
};

/** Supprimer un prospect */
export const supprimerProspect = async (prospectId: string): Promise<void> => {
  await deleteDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  console.log(`[CRM] Prospect supprimé: ${prospectId}`);
};

// ============================================
// PURGE — Remise à zéro des prospects
// ============================================

/** Supprimer tous les prospects (remise à zéro avant re-saisie avec nouveaux champs) */
export const purgerTousLesProspects = async (): Promise<number> => {
  const snap = await getDocs(collection(db, COLLECTION_PROSPECTS));
  let count = 0;
  for (const d of snap.docs) {
    await deleteDoc(doc(db, COLLECTION_PROSPECTS, d.id));
    count++;
  }
  console.log(`[CRM] Purge terminée : ${count} prospects supprimés`);
  return count;
};

// ============================================
// NOTES DE SUIVI
// ============================================

/** Ajouter une note de suivi à un prospect */
export const ajouterNote = async (
  prospectId: string,
  texte: string,
  auteur: ResponsableProspect
): Promise<void> => {
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) throw new Error('Prospect introuvable');

  const data = prospectSnap.data();
  const notes: NoteSuivi[] = data.notes || [];

  notes.push({
    id: `note_${Date.now()}`,
    date: Timestamp.now(),
    texte: texte.trim(),
    auteur,
  });

  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    notes,
    updatedAt: Timestamp.now(),
  });
  console.log(`[CRM] Note ajoutée au prospect ${prospectId}`);
};

export const supprimerNote = async (
  prospectId: string,
  noteId: string
): Promise<void> => {
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) throw new Error('Prospect introuvable');
  const notes: NoteSuivi[] = (prospectSnap.data().notes || []).filter((n: NoteSuivi) => n.id !== noteId);
  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), { notes, updatedAt: Timestamp.now() });
  console.log(`[CRM] Note ${noteId} supprimée du prospect ${prospectId}`);
};

export const modifierNote = async (
  prospectId: string,
  noteId: string,
  nouveauTexte: string
): Promise<void> => {
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) throw new Error('Prospect introuvable');
  const notes: NoteSuivi[] = (prospectSnap.data().notes || []).map((n: NoteSuivi) =>
    n.id === noteId ? { ...n, texte: nouveauTexte.trim() } : n
  );
  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), { notes, updatedAt: Timestamp.now() });
  console.log(`[CRM] Note ${noteId} modifiée sur le prospect ${prospectId}`);
};

// ============================================
// HISTORIQUE MAILS SENTINELLE
// ============================================

/** Ajouter une entrée dans l'historique mails d'un prospect — retourne l'ID créé */
export const ajouterHistoriqueMail = async (
  prospectId: string,
  data: {
    objet: string;
    template: TypeTemplateMail;
    destinataire: string;
    auteur: ResponsableProspect;
    envoye: boolean;
    apercu?: string;
    contenuHtml?: string;
  }
): Promise<string> => {
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) throw new Error('Prospect introuvable');

  const prospectData = prospectSnap.data();
  const historiqueMails: HistoriqueMailProspect[] = prospectData.historiqueMails;

  const mailId = `mail_${Date.now()}`;
  historiqueMails.push({
    id: mailId,
    date: Timestamp.now(),
    objet: data.objet.trim(),
    template: data.template,
    destinataire: data.destinataire.trim(),
    auteur: data.auteur,
    envoye: data.envoye,
    apercu: data.apercu || '',
    ...(data.contenuHtml ? { contenuHtml: data.contenuHtml } : {}),
  });

  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    historiqueMails,
    updatedAt: Timestamp.now(),
  });
  console.log(`[CRM] Mail ajouté à l'historique du prospect ${prospectId} — ${data.template} — id: ${mailId}`);
  return mailId;
};

/** Marquer un mail comme envoyé (si généré puis confirmé) */
export const marquerMailEnvoye = async (
  prospectId: string,
  mailId: string,
  contenuReel?: string
): Promise<void> => {
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) throw new Error('Prospect introuvable');

  const prospectData = prospectSnap.data();
  const historiqueMails: HistoriqueMailProspect[] = prospectData.historiqueMails;

  const index = historiqueMails.findIndex((m) => m.id === mailId);
  if (index === -1) throw new Error('Mail introuvable dans l\'historique');

  historiqueMails[index].envoye = true;
  historiqueMails[index].type = 'envoye';
  // Si l'utilisateur a fourni le contenu réellement envoyé, on l'archive
  if (contenuReel && contenuReel.trim()) {
    historiqueMails[index].apercu = contenuReel.trim();
  }

  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    historiqueMails,
    updatedAt: Timestamp.now(),
  });
  console.log(`[CRM] Mail ${mailId} marqué comme envoyé — prospect ${prospectId}`);
};

/** Supprimer un mail de l'historique d'un prospect */
export const supprimerMailHistorique = async (
  prospectId: string,
  mailId: string
): Promise<void> => {
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) throw new Error('Prospect introuvable');

  const prospectData = prospectSnap.data();
  const historiqueMails: HistoriqueMailProspect[] = prospectData.historiqueMails;

  const index = historiqueMails.findIndex((m) => m.id === mailId);
  if (index === -1) throw new Error('Mail introuvable dans l\'historique');

  historiqueMails.splice(index, 1);

  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    historiqueMails,
    updatedAt: Timestamp.now(),
  });
  console.log(`[CRM] Mail ${mailId} supprimé de l'historique — prospect ${prospectId}`);
};

// ============================================
// RÉPONSES ENTRANTES — SESSION 206
// Upload PDF dans Storage + entrée 'recu' dans historiqueMails
// Analyse SENTINELLE persistée via enregistrerAnalyseReponse()
// ============================================

/**
 * Importe une réponse reçue d'un prospect (PDF exporté depuis Gmail).
 * 1. Upload le PDF en Storage : crm/prospects/{prospectId}/reponses/{mailId}.pdf
 * 2. Pousse une entrée type='recu' dans historiqueMails du prospect
 * Retourne l'id du mail créé et l'URL du PDF — permet l'appel SENTINELLE côté page.
 */
export const importerReponseProspect = async (
  prospectId: string,
  fichier: File,
  auteur: ResponsableProspect
): Promise<{ mailId: string; fichierPdfUrl: string }> => {
  const mailId = `recu_${Date.now()}`;
  const storagePath = `crm/prospects/${prospectId}/reponses/${mailId}.pdf`;

  // Upload Storage
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, fichier);
  const fichierPdfUrl = await getDownloadURL(storageRef);

  // Entrée dans historiqueMails
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) throw new Error('Prospect introuvable');

  const prospectData = prospectSnap.data();
  const historiqueMails: HistoriqueMailProspect[] = prospectData.historiqueMails || [];

  historiqueMails.push({
    id: mailId,
    date: Timestamp.now(),
    objet: fichier.name.replace(/\.pdf$/i, ''),
    template: 'libre',
    destinataire: '',
    auteur,
    envoye: false,
    type: 'recu',
    fichierPdfUrl,
  });

  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    historiqueMails,
    updatedAt: Timestamp.now(),
  });

  console.log(`[CRM] Réponse importée — prospect ${prospectId} — mailId ${mailId}`);
  return { mailId, fichierPdfUrl };
};

/**
 * Persiste l'analyse SENTINELLE sur un mail de type 'recu'.
 * Appelée après le retour de l'API Claude depuis la page.
 */
export const enregistrerAnalyseReponse = async (
  prospectId: string,
  mailId: string,
  analyse: AnalyseReponseProspect
): Promise<void> => {
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) throw new Error('Prospect introuvable');

  const prospectData = prospectSnap.data();
  const historiqueMails: HistoriqueMailProspect[] = prospectData.historiqueMails || [];

  const index = historiqueMails.findIndex((m) => m.id === mailId);
  if (index === -1) throw new Error(`Mail ${mailId} introuvable dans l'historique`);

  historiqueMails[index] = {
    ...historiqueMails[index],
    analyseReponse: analyse,
  };

  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    historiqueMails,
    updatedAt: Timestamp.now(),
  });

  console.log(`[CRM] Analyse SENTINELLE persistée — prospect ${prospectId} — mail ${mailId}`);
};

// ============================================
// DOCUMENTS PROSPECT — SESSION 199
// Stockage : Firebase Storage crm/prospects/{prospectId}/{docId}.pdf
// Métadonnées : sous-collection crm_prospects/{prospectId}/documents/{docId}
// ============================================

/**
 * Dépose un fichier PDF dans le dossier prospect.
 * Upload Storage + écriture métadonnées Firestore.
 */
export const ajouterDocumentProspect = async (
  prospectId: string,
  fichier: File,
  type: TypeDocumentProspect,
  nom: string,
  auteur: ResponsableProspect
): Promise<DocumentProspect> => {
  const docId = `doc_${Date.now()}`;
  const fichierNom = `${docId}.pdf`;
  const storagePath = `crm/prospects/${prospectId}/${fichierNom}`;

  // Upload vers Firebase Storage
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, fichier);
  const storageUrl = await getDownloadURL(storageRef);

  const document: DocumentProspect = {
    id: docId,
    type,
    nom: nom.trim(),
    fichierNom,
    storageUrl,
    taille: fichier.size,
    auteur,
    createdAt: Timestamp.now(),
  };

  // Persister les métadonnées dans la sous-collection
  await setDoc(
    doc(db, COLLECTION_PROSPECTS, prospectId, 'documents', docId),
    document
  );

  // Mettre à jour updatedAt sur le prospect principal
  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    updatedAt: Timestamp.now(),
  });

  console.log(`[CRM] Document ajouté au prospect ${prospectId} — ${nom} (${type})`);
  return document;
};

/**
 * Charge tous les documents d'un prospect, triés par date décroissante.
 */
export const chargerDocumentsProspect = async (
  prospectId: string
): Promise<DocumentProspect[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_PROSPECTS, prospectId, 'documents'),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as DocumentProspect);
  } catch (error) {
    console.error(`[CRM] Erreur chargement documents prospect ${prospectId}:`, error);
    return [];
  }
};

/**
 * Supprime un document prospect (Storage + Firestore).
 */
export const supprimerDocumentProspect = async (
  prospectId: string,
  document: DocumentProspect
): Promise<void> => {
  // Supprimer le fichier en Storage
  try {
    const storagePath = `crm/prospects/${prospectId}/${document.fichierNom}`;
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (storageError) {
    // Non bloquant — si le fichier est déjà absent en Storage, on continue
    console.warn(`[CRM] Fichier Storage absent, suppression Firestore uniquement:`, storageError);
  }

  // Supprimer les métadonnées Firestore
  await deleteDoc(
    doc(db, COLLECTION_PROSPECTS, prospectId, 'documents', document.id)
  );

  console.log(`[CRM] Document ${document.id} supprimé — prospect ${prospectId}`);
};

// ============================================
// CONVERSATION SENTINELLE CRM — SESSION 199
// Stockage : sous-collection crm_prospects/{prospectId}/conversation/{messageId}
// Persistée entre les sessions — chaque ouverture de fiche recharge l'historique.
// ============================================

/**
 * Charge l'historique de la conversation SENTINELLE d'un prospect.
 * Triée chronologiquement (ordre naturel de la conversation).
 */
export const chargerConversationProspect = async (
  prospectId: string
): Promise<MessageConversationProspect[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_PROSPECTS, prospectId, 'conversation'),
      orderBy('createdAt', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as MessageConversationProspect);
  } catch (error) {
    console.error(`[CRM] Erreur chargement conversation prospect ${prospectId}:`, error);
    return [];
  }
};

/**
 * Persiste un message dans la conversation SENTINELLE d'un prospect.
 * Appelé après chaque échange (user + assistant).
 */
export const ajouterMessageConversation = async (
  prospectId: string,
  role: 'user' | 'assistant',
  content: string,
  documentId?: string
): Promise<MessageConversationProspect> => {
  const messageId = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

  const message: MessageConversationProspect = {
    id: messageId,
    role,
    content,
    createdAt: Timestamp.now(),
    ...(documentId ? { documentId } : {}),
  };

  await setDoc(
    doc(db, COLLECTION_PROSPECTS, prospectId, 'conversation', messageId),
    message
  );

  return message;
};

/**
 * Efface toute la conversation SENTINELLE d'un prospect.
 * Action explicite — bouton "Réinitialiser la conversation" dans la fiche.
 */
export const reinitialiserConversationProspect = async (
  prospectId: string
): Promise<void> => {
  const snap = await getDocs(
    collection(db, COLLECTION_PROSPECTS, prospectId, 'conversation')
  );
  for (const d of snap.docs) {
    await deleteDoc(d.ref);
  }
  console.log(`[CRM] Conversation SENTINELLE réinitialisée — prospect ${prospectId}`);
};

// ============================================
// RAPPROCHEMENT AUTOMATIQUE — PROSPECT ↔ USER
// ============================================

export const rapprocherProspectUser = async (
  prospectId: string
): Promise<{ found: boolean; userId?: string; formule?: string }> => {
  const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
  if (!prospectSnap.exists()) return { found: false };

  const prospect = prospectSnap.data();
  const emailRecherche = (prospect.contactEmail || '').trim().toLowerCase();

  if (!emailRecherche) {
    console.warn('[CRM] Pas d\'email sur le prospect, rapprochement impossible');
    return { found: false };
  }

  try {
    const q = query(
      collection(db, 'users'),
      where('email', '==', emailRecherche)
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      console.log(`[CRM] Aucun user trouvé pour ${emailRecherche}`);
      return { found: false };
    }

    const userDoc = snap.docs[0];
    const userData = userDoc.data();
    const userId = userDoc.id;
    const formule = userData.formuleAbonnement || 'inconnue';

    await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
      clientUserId: userId,
      clientEmail: emailRecherche,
      formuleClient: formule,
      statut: 'converti',
      dateConversion: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    await updateDoc(doc(db, 'users', userId), {
      apporteurId: prospect.responsable,
      apporteurProspectId: prospectId,
      dateAttribution: Timestamp.now(),
    });

    console.log(`[CRM] Rapprochement réussi: prospect ${prospectId} ↔ user ${userId} (${formule})`);
    return { found: true, userId, formule };
  } catch (error) {
    console.error('[CRM] Erreur rapprochement:', error);
    return { found: false };
  }
};

// ============================================
// DÉTECTION USERS ACTIFS SANS APPORTEUR — SESSION 225
// ============================================

/**
 * Retourne la liste des users avec statutAbonnement === 'actif'
 * qui n'ont pas de champ apporteurId renseigné.
 * Utilisé dans DashboardCommissionsPage pour alerter l'admin.
 */
export const chargerUsersActifsSansApporteur = async (): Promise<
  { userId: string; email: string; displayName: string; formule: string }[]
> => {
  try {
    const q = query(
      collection(db, 'users'),
      where('statutAbonnement', '==', 'actif')
    );
    const snap = await getDocs(q);
    const result: { userId: string; email: string; displayName: string; formule: string }[] = [];
    for (const d of snap.docs) {
      const data = d.data();
      if (!data.apporteurId) {
        result.push({
          userId: d.id,
          email: data.email || '',
          displayName: data.displayName || data.email || 'Inconnu',
          formule: data.formuleAbonnement || 'Non renseigné',
        });
      }
    }
    return result;
  } catch (error) {
    console.error('[CRM] Erreur détection users sans apporteur:', error);
    return [];
  }
};

// ============================================
// COMMISSIONS — CALCUL ET SUIVI
// ============================================

export const calculerCommissionsTrimestre = async (
  trimestre: string,
  apporteurId: string
): Promise<{
  montantDu: number;
  details: DetailCommissionClient[];
}> => {
  const details: DetailCommissionClient[] = [];
  let montantDu = 0;

  try {
    const q = query(
      collection(db, 'users'),
      where('apporteurId', '==', apporteurId)
    );
    const snap = await getDocs(q);

    for (const userDoc of snap.docs) {
      const userData = userDoc.data();

      if (userData.statutAbonnement !== 'actif') {
        details.push({
          userId: userDoc.id,
          nomClient: userData.displayName || userData.email || 'Inconnu',
          formule: userData.formuleAbonnement || 'inconnue',
          caHTMensuel: 0,
          commissionMensuelle: 0,
          statutAbonnement: userData.statutAbonnement || 'inconnu',
        });
        continue;
      }

      const formule = userData.formuleAbonnement || '';
      const caHTMensuel = calculerCAHTMensuel(formule);
      const commissionMensuelle = calculerCommissionMensuelle(formule);
      const commissionTrimestrielle = commissionMensuelle * 3;
      montantDu += commissionTrimestrielle;

      details.push({
        userId: userDoc.id,
        nomClient: userData.displayName || userData.email || 'Inconnu',
        formule,
        caHTMensuel,
        commissionMensuelle,
        statutAbonnement: 'actif',
      });
    }
  } catch (error) {
    console.error('[CRM] Erreur calcul commissions:', error);
  }

  return {
    montantDu: Math.round(montantDu * 100) / 100,
    details,
  };
};

export const enregistrerCommission = async (
  trimestre: string,
  apporteurId: string,
  apporteurNom: string
): Promise<string> => {
  const { montantDu, details } = await calculerCommissionsTrimestre(trimestre, apporteurId);

  const q = query(
    collection(db, COLLECTION_COMMISSIONS),
    where('apporteurId', '==', apporteurId),
    where('trimestre', '==', trimestre)
  );
  const snap = await getDocs(q);

  const now = Timestamp.now();

  if (!snap.empty) {
    const existingId = snap.docs[0].id;
    const existingData = snap.docs[0].data();

    await updateDoc(doc(db, COLLECTION_COMMISSIONS, existingId), {
      montantDu,
      resteAVerser: montantDu - (existingData.montantVerse || 0),
      details,
      updatedAt: now,
    });

    console.log(`[CRM] Commission ${trimestre} mise à jour: ${montantDu}€`);
    return existingId;
  } else {
    const commission = {
      apporteurId,
      apporteurNom,
      trimestre,
      montantDu,
      montantVerse: 0,
      resteAVerser: montantDu,
      details,
      statut: 'du',
      createdAt: now,
      updatedAt: now,
    };

    const ref = await addDoc(collection(db, COLLECTION_COMMISSIONS), commission);
    console.log(`[CRM] Commission ${trimestre} créée: ${ref.id} — ${montantDu}€`);
    return ref.id;
  }
};

export const marquerCommissionVersee = async (
  commissionId: string,
  montantVerse: number,
  referenceVersement?: string
): Promise<void> => {
  const commissionSnap = await getDoc(doc(db, COLLECTION_COMMISSIONS, commissionId));
  if (!commissionSnap.exists()) throw new Error('Commission introuvable');

  const data = commissionSnap.data();
  const totalVerse = (data.montantVerse || 0) + montantVerse;
  const resteAVerser = Math.max(0, data.montantDu - totalVerse);
  const statut = resteAVerser === 0 ? 'verse' : 'partiel';

  await updateDoc(doc(db, COLLECTION_COMMISSIONS, commissionId), {
    montantVerse: Math.round(totalVerse * 100) / 100,
    resteAVerser: Math.round(resteAVerser * 100) / 100,
    statut,
    dateVersement: Timestamp.now(),
    referenceVersement: referenceVersement || '',
    updatedAt: Timestamp.now(),
  });

  console.log(`[CRM] Commission ${commissionId}: ${montantVerse}€ versé — statut: ${statut}`);
};

export const chargerCommissions = async (
  apporteurId?: string
): Promise<Commission[]> => {
  try {
    let q;
    if (apporteurId) {
      q = query(
        collection(db, COLLECTION_COMMISSIONS),
        where('apporteurId', '==', apporteurId),
        orderBy('trimestre', 'desc')
      );
    } else {
      q = query(
        collection(db, COLLECTION_COMMISSIONS),
        orderBy('trimestre', 'desc')
      );
    }

    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as Commission[];
  } catch (error) {
    console.error('[CRM] Erreur chargement commissions:', error);
    return [];
  }
};

// ============================================
// LIAISON MANUELLE PROSPECT ↔ USER — Session 207
// ============================================

/**
 * Lie manuellement un prospect converti à un compte utilisateur Firestore.
 * Appelé depuis ApporteurDetailPage (admin) via saisie du userId.
 * Distinct de rapprocherProspectUser (auto par email) — ici c'est l'admin qui décide.
 *
 * Met à jour :
 *   - crm_prospects/{prospectId} : clientUserId, clientEmail, formuleClient
 *   - users/{userId}             : apporteurId, apporteurProspectId, dateAttribution
 */
export const lierProspectUserManuellement = async (
  prospectId: string,
  userId: string
): Promise<{ succes: boolean; formule?: string; email?: string; erreur?: string }> => {
  try {
    // 1. Lire le user cible
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) {
      return { succes: false, erreur: 'Aucun utilisateur trouvé avec cet identifiant.' };
    }
    const userData = userSnap.data();
    const formule: string = userData.formuleAbonnement || 'inconnue';
    const email: string = (userData.email || '').trim().toLowerCase();

    // 2. Lire le prospect pour récupérer le responsable
    const prospectSnap = await getDoc(doc(db, COLLECTION_PROSPECTS, prospectId));
    if (!prospectSnap.exists()) {
      return { succes: false, erreur: 'Prospect introuvable.' };
    }
    const prospectData = prospectSnap.data();

    // 3. Mettre à jour le prospect
    await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
      clientUserId: userId,
      clientEmail: email,
      formuleClient: formule,
      updatedAt: Timestamp.now(),
    });

    // 4. Mettre à jour le user (lien retour)
    await updateDoc(doc(db, 'users', userId), {
      apporteurId: prospectData.responsable || null,
      apporteurProspectId: prospectId,
      dateAttribution: Timestamp.now(),
    });

    console.log(`[CRM] Liaison manuelle : prospect ${prospectId} ↔ user ${userId} (${formule})`);
    return { succes: true, formule, email };
  } catch (error) {
    console.error('[CRM] Erreur liaison manuelle:', error);
    return { succes: false, erreur: 'Erreur technique lors de la liaison.' };
  }
};

/**
 * Délie un prospect d'un compte utilisateur (annulation de liaison).
 * Remet clientUserId, clientEmail et formuleClient à undefined.
 */
export const delierProspectUser = async (
  prospectId: string
): Promise<void> => {
  await updateDoc(doc(db, COLLECTION_PROSPECTS, prospectId), {
    clientUserId: null,
    clientEmail: null,
    formuleClient: null,
    updatedAt: Timestamp.now(),
  });
};

// ============================================
// UTILITAIRES
// ============================================

export const getTrimestreCourant = (): string => {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${q}`;
};

export const getStatsPipeline = (prospects: Prospect[]): Record<StatutProspect, number> => {
  const stats: Record<StatutProspect, number> = {
    identifie: 0,
    contacte: 0,
    interesse: 0,
    demo: 0,
    converti: 0,
    perdu: 0,
  };

  for (const p of prospects) {
    if (stats[p.statut] !== undefined) {
      stats[p.statut]++;
    }
  }

  return stats;
};
